/**
 * 任务编排器 - 通过 Hook 驱动 Claude Code 持续工作
 *
 * 设计理念：
 * 1. workflow-cli 负责任务规划和编排
 * 2. Claude Code 负责实际的代码编写
 * 3. 通过 Hook 机制实现双向通信
 */

import { EventEmitter } from 'events';
import path from 'path';
import fs from 'fs-extra';
import { logger } from '../utils/logger';
import { Task, TaskPriority } from '../types';
import { WorkflowManager } from './workflow-manager';
import { TaskStore } from './task-store';
import { v4 as uuidv4 } from 'uuid';

export interface TaskInstruction {
  id: string;
  task: Task;
  instruction: string;
  context: any;
  createdAt: Date;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
}

export interface OrchestratorOptions {
  projectRoot?: string;
  maxConcurrentTasks?: number;
  taskTimeout?: number; // minutes
  autoAssignNext?: boolean;
}

export class TaskOrchestrator extends EventEmitter {
  private projectRoot: string;
  private instructionFile: string;
  private stateFile: string;
  private workflowManager: WorkflowManager;
  private taskStore: TaskStore;
  private currentInstruction: TaskInstruction | null = null;
  private taskQueue: Task[] = [];
  private isRunning = false;
  private retryCount = 0;
  private tasks: Task[] = [];

  constructor(private options: OrchestratorOptions = {}) {
    super();
    this.projectRoot = options.projectRoot || process.cwd();
    this.instructionFile = path.join(this.projectRoot, '.claude/current-task.md');
    this.stateFile = path.join(this.projectRoot, '.workflow/orchestrator-state.json');
    this.workflowManager = new WorkflowManager({ projectRoot: this.projectRoot });
    this.taskStore = new TaskStore(this.projectRoot);
  }

  async initialize(): Promise<void> {
    await fs.ensureDir(path.dirname(this.instructionFile));
    await fs.ensureDir(path.dirname(this.stateFile));

    // 加载状态
    await this.loadState();

    // 设置 Hook 配置
    await this.setupHooks();

    logger.info('Task orchestrator initialized');
  }

  /**
   * 设置 Claude Code Hook
   */
  private async setupHooks(): Promise<void> {
    const settingsPath = path.join(this.projectRoot, '.claude/settings.local.json');
    let settings: any = {};

    if (await fs.pathExists(settingsPath)) {
      settings = await fs.readJson(settingsPath);
    }

    // 确保 hooks 结构存在
    if (!settings.hooks) {
      settings.hooks = {};
    }

    // 添加任务完成检测 Hook
    settings.hooks.PostToolUse = settings.hooks.PostToolUse || [];

    // 检测代码修改
    const codeEditHook = {
      matcher: '^(Edit|Write|MultiEdit)',
      hooks: [
        {
          type: 'command',
          command: `cd "${this.projectRoot}" && npx @gongfu/workflow-cli orchestrate check-progress`,
          timeout: 5,
        },
      ],
    };

    // 检测任务完成标记
    const taskCompleteHook = {
      matcher: '^TodoWrite',
      hooks: [
        {
          type: 'command',
          command: `cd "${this.projectRoot}" && npx @gongfu/workflow-cli orchestrate task-completed`,
          timeout: 3,
        },
      ],
    };

    // 添加权限到 allow 列表
    if (!settings.permissions) {
      settings.permissions = { allow: [], deny: [] };
    }

    // 确保权限列表是数组
    if (!Array.isArray(settings.permissions.allow)) {
      settings.permissions.allow = [];
    }

    // 添加必要的权限
    const requiredPermissions = [
      'Bash(cd:*)',
      'Bash(npx:*)',
      'Bash(npx @gongfu/workflow-cli:*)',
      'Bash(npx @gongfu/workflow-cli orchestrate:*)',
      'Edit(**)',
      'Write(**)',
      'MultiEdit(**)',
      'TodoWrite',
    ];

    requiredPermissions.forEach(perm => {
      if (!settings.permissions.allow.includes(perm)) {
        settings.permissions.allow.push(perm);
      }
    });

    // 添加 Hooks（避免重复）
    if (!settings.hooks.PostToolUse.some((h: any) => h.matcher === codeEditHook.matcher)) {
      settings.hooks.PostToolUse.push(codeEditHook);
    }

    if (!settings.hooks.PostToolUse.some((h: any) => h.matcher === taskCompleteHook.matcher)) {
      settings.hooks.PostToolUse.push(taskCompleteHook);
    }

    // 会话开始 Hook
    settings.hooks.Start = settings.hooks.Start || [];
    settings.hooks.Start.push({
      hooks: [
        {
          type: 'command',
          command: `cd "${this.projectRoot}" && npx @gongfu/workflow-cli orchestrate session-start`,
          timeout: 3,
        },
      ],
    });

    await fs.writeJson(settingsPath, settings, { spaces: 2 });
  }

  /**
   * 开始任务编排
   */
  async start(): Promise<void> {
    this.isRunning = true;
    logger.info('Starting task orchestration');

    // 检查是否有未完成的任务
    if (this.currentInstruction?.status === 'in_progress') {
      logger.info('Resuming previous task');
      await this.updateInstruction();
    } else {
      // 分配新任务
      await this.assignNextTask();
    }

    // 启动监控
    this.startMonitoring();
  }

  /**
   * 停止任务编排
   */
  async stop(): Promise<void> {
    this.isRunning = false;
    await this.saveState();
    logger.info('Task orchestration stopped');
  }

  /**
   * 分配下一个任务
   */
  async assignNextTask(): Promise<void> {
    // 初始化 TaskStore
    await this.taskStore.initialize();
    
    // 从 TaskStore 获取最新任务列表
    this.tasks = await this.taskStore.getTasks();
    
    // 从队列获取任务
    let nextTask = this.taskQueue.shift();

    if (!nextTask) {
      // 首先检查是否有当前正在进行的任务
      const taskState = await this.workflowManager.getTaskState();
      if (taskState?.current) {
        // 有当前任务，创建任务对象
        nextTask = {
          id: taskState.current.id || uuidv4(),
          content: taskState.current.task,
          priority: taskState.current.priority,
          status: 'in_progress',
          position: 0,
        };
      } else {
        // 从 WorkflowManager 获取推荐任务
        const recommendations = await this.workflowManager.recommendNextTask();
        if (recommendations && recommendations.length > 0) {
          // 检查是否已存在相同内容的任务
          const existingTask = this.tasks.find(t => 
            t.content === recommendations[0].task && 
            (t.status === 'todo' || t.status === 'in_progress')
          );
          
          if (existingTask) {
            nextTask = existingTask;
          } else {
            // 直接创建任务对象
            const newTask: Task = {
              id: uuidv4(),
              content: recommendations[0].task,
              priority: recommendations[0].priority,
              status: 'todo',
              position: this.tasks.length,
              tags: ['auto-generated', '24h-mode'],
              createdAt: new Date(),
              updatedAt: new Date()
            };
            
            // 手动添加到任务列表并保存，避免触发 webhook
            this.tasks.push(newTask);
            const tasksPath = path.join(this.projectRoot, '.workflow/tasks.json');
            await fs.writeJson(tasksPath, this.tasks, { spaces: 2 });
            
            // 更新 TaskStore 的内存缓存（重新初始化）
            await this.taskStore.initialize();
            
            nextTask = newTask;
            logger.info(`Created new task via TaskStore: ${nextTask.content}`);
          }
        }
      }
    }

    if (!nextTask) {
      logger.info('No more tasks available');
      if (this.options.autoAssignNext) {
        // 增加重试间隔，避免过于频繁的检查
        const retryDelay = Math.min(300000, (this.retryCount || 0) * 60000); // 最长5分钟
        this.retryCount = (this.retryCount || 0) + 1;
        logger.info(`Waiting ${retryDelay / 1000}s before next check...`);
        setTimeout(() => this.assignNextTask(), retryDelay);
      } else {
        this.retryCount = 0; // 重置重试计数
      }
      return;
    }
    
    // 有新任务时重置重试计数
    this.retryCount = 0;

    // 创建任务指令
    const instruction = await this.createTaskInstruction(nextTask);
    this.currentInstruction = instruction;

    // 写入指令文件
    await this.updateInstruction();

    // 通知 Claude Code
    this.emit('task-assigned', instruction);

    logger.info(`Assigned task: ${nextTask.content}`);
  }

  /**
   * 创建任务指令
   */
  private async createTaskInstruction(task: Task): Promise<TaskInstruction> {
    // 分析任务
    const estimation = await this.workflowManager.estimateTaskTime(task.content, task.priority);

    // 获取项目上下文
    const context = {
      projectType: await this.detectProjectType(),
      language: await this.detectLanguage(),
      framework: await this.detectFramework(),
      testCommand: await this.getTestCommand(),
      lintCommand: await this.getLintCommand(),
    };

    // 生成详细指令
    const instruction = this.generateInstruction(task, estimation, context);

    return {
      id: uuidv4(),
      task,
      instruction,
      context,
      createdAt: new Date(),
      status: 'in_progress',
    };
  }

  /**
   * 生成任务指令
   */
  private generateInstruction(task: Task, estimation: any, context: any): string {
    const complexity = estimation?.complexity;
    const timeEstimate = complexity?.estimatedTime || 60;

    return `# 当前任务

## 任务描述
${task.content}

## 任务信息
- ID: ${task.id}
- 优先级: ${task.priority}
- 预计时间: ${timeEstimate} 分钟
- 复杂度: ${complexity?.score || 50}/100

## 完成步骤

### 1. 理解阶段 (Understanding)
- 仔细阅读任务需求
- 搜索相关代码和文档
- 理解现有实现

### 2. 思考阶段 (Thinking)
- 设计实现方案
- 考虑边界情况
- 规划测试策略

### 3. 组织阶段 (Organizing)
- 列出需要修改的文件
- 准备必要的依赖
- 创建任务清单

### 4. 编程阶段 (Programming)
- 实现核心功能
- 编写必要的测试
- 添加代码注释

### 5. 检查阶段 (Inspecting)
- 运行 ${context.lintCommand || 'npm run lint'}
- 运行 ${context.testCommand || 'npm test'}
- 确保代码质量

### 6. 归档阶段 (Archiving)
- 更新相关文档
- 提交代码（如需要）
- 标记任务完成

## 项目上下文
- 项目类型: ${context.projectType}
- 主要语言: ${context.language}
- 框架: ${context.framework}

## 完成标准
1. 功能已实现并测试通过
2. 代码符合项目规范
3. 相关文档已更新
4. 无 lint 错误和类型错误

## 任务完成标记
当任务完成后，请在代码中添加注释：
\`\`\`
// Task ${task.id} completed by Claude Code
\`\`\`

或更新 TODO 文件标记任务完成。

---
**提示**: 这是由 workflow-cli 自动生成的任务指令。完成后系统会自动分配下一个任务。
`;
  }

  /**
   * 更新指令文件
   */
  private async updateInstruction(): Promise<void> {
    if (!this.currentInstruction) return;

    await fs.writeFile(this.instructionFile, this.currentInstruction.instruction);

    // 同时创建一个更简洁的提示文件
    const promptFile = path.join(this.projectRoot, '.claude/task-prompt.md');
    const simplePrompt = `请完成以下任务：

${this.currentInstruction.task.content}

完成后在代码中添加注释：// Task ${this.currentInstruction.task.id} completed`;

    await fs.writeFile(promptFile, simplePrompt);
  }

  /**
   * 检查任务进度
   */
  async checkProgress(): Promise<void> {
    if (!this.currentInstruction) return;

    // 检查是否有完成标记
    const hasCompletionMarker = await this.findCompletionMarker(this.currentInstruction.task.id);

    if (hasCompletionMarker) {
      await this.markTaskCompleted();
    } else {
      // 更新进度
      const elapsed = Date.now() - this.currentInstruction.createdAt.getTime();
      const timeoutMs = (this.options.taskTimeout || 120) * 60 * 1000;

      if (elapsed > timeoutMs) {
        logger.warn('Task timeout reached');
        await this.handleTaskTimeout();
      }
    }
  }

  /**
   * 查找完成标记
   */
  private async findCompletionMarker(taskId: string): Promise<boolean> {
    const searchPattern = `Task ${taskId} completed`;

    try {
      const { execSync } = require('child_process');
      // 使用 grep 替代 ripgrep
      const result = execSync(
        `grep -r "${searchPattern}" --include="*.ts" --include="*.js" --include="*.tsx" --include="*.jsx" src/ 2>/dev/null || true`,
        { cwd: this.projectRoot, encoding: 'utf-8' }
      );

      return result.includes(searchPattern);
    } catch {
      return false;
    }
  }

  /**
   * 标记任务完成
   */
  async markTaskCompleted(): Promise<void> {
    if (!this.currentInstruction) return;

    logger.info(`Task completed: ${this.currentInstruction.task.content}`);

    // 更新状态
    this.currentInstruction.status = 'completed';

    // 更新 WorkflowManager
    await this.workflowManager.completeCurrentTask('Completed by Claude Code');

    // 保存状态
    await this.saveState();

    // 发送通知
    this.emit('task-completed', this.currentInstruction);

    // 清理指令文件
    await fs.remove(this.instructionFile);

    // 自动分配下一个任务
    if (this.isRunning && this.options.autoAssignNext) {
      setTimeout(() => this.assignNextTask(), 5000);
    }
  }

  /**
   * 处理任务超时
   */
  private async handleTaskTimeout(): Promise<void> {
    if (!this.currentInstruction) return;

    logger.warn(`Task timeout: ${this.currentInstruction.task.content}`);

    this.currentInstruction.status = 'failed';

    // 可以选择重试或跳过
    this.emit('task-timeout', this.currentInstruction);

    // 分配下一个任务
    if (this.options.autoAssignNext) {
      await this.assignNextTask();
    }
  }

  /**
   * 监控任务执行
   */
  private startMonitoring(): void {
    // 定期检查进度
    setInterval(() => {
      if (this.isRunning && this.currentInstruction) {
        this.checkProgress();
      }
    }, 60000); // 每分钟检查一次
  }

  /**
   * 保存状态
   */
  private async saveState(): Promise<void> {
    const state = {
      isRunning: this.isRunning,
      currentInstruction: this.currentInstruction,
      taskQueue: this.taskQueue,
      lastUpdate: new Date(),
    };

    await fs.writeJson(this.stateFile, state, { spaces: 2 });
  }

  /**
   * 加载状态
   */
  private async loadState(): Promise<void> {
    if (await fs.pathExists(this.stateFile)) {
      const state = await fs.readJson(this.stateFile);
      this.currentInstruction = state.currentInstruction;
      this.taskQueue = state.taskQueue || [];
    }
  }

  // 辅助方法
  private async detectProjectType(): Promise<string> {
    if (await fs.pathExists(path.join(this.projectRoot, 'package.json'))) {
      return 'node';
    }
    return 'unknown';
  }

  private async detectLanguage(): Promise<string> {
    if (await fs.pathExists(path.join(this.projectRoot, 'tsconfig.json'))) {
      return 'typescript';
    }
    return 'javascript';
  }

  private async detectFramework(): Promise<string> {
    try {
      const pkg = await fs.readJson(path.join(this.projectRoot, 'package.json'));
      if (pkg.dependencies?.react || pkg.devDependencies?.react) return 'react';
      if (pkg.dependencies?.vue || pkg.devDependencies?.vue) return 'vue';
      if (pkg.dependencies?.next || pkg.devDependencies?.next) return 'nextjs';
    } catch {}
    return 'none';
  }

  private async getTestCommand(): Promise<string> {
    try {
      const pkg = await fs.readJson(path.join(this.projectRoot, 'package.json'));
      return pkg.scripts?.test || 'npm test';
    } catch {
      return 'npm test';
    }
  }

  private async getLintCommand(): Promise<string> {
    try {
      const pkg = await fs.readJson(path.join(this.projectRoot, 'package.json'));
      return pkg.scripts?.lint || 'npm run lint';
    } catch {
      return 'npm run lint';
    }
  }

  /**
   * Hook 回调方法
   */
  async onSessionStart(): Promise<void> {
    logger.info('Claude Code session started');
    if (this.options.autoAssignNext && !this.currentInstruction) {
      await this.assignNextTask();
    }
  }

  async onTaskCompleted(): Promise<void> {
    await this.markTaskCompleted();
  }

  async onCheckProgress(): Promise<void> {
    await this.checkProgress();
  }
}
